import React, { useEffect, useRef, useState } from "react";
import baseUrl from "../../../../api/baseUrl";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { UI } from "../components/appointmentScheduleUi";
import {
  PATIENT_TYPE_STYLES,
  formatPatientTypeLabel,
  resolvePatientTypeLabelFromPatientAndTreatments,
} from "../../viewappointment/components/appointmentViewShared";

const SEARCH_DEBOUNCE_MS = 280;

function PatientTypeBadges({
  patientTypeLabel,
}: {
  patientTypeLabel?: string | null;
}) {
  const showVisit =
    patientTypeLabel === "Treatment Plan" ||
    patientTypeLabel === "New Patient" ||
    patientTypeLabel === "Existing Patient";

  if (!showVisit) return null;

  return (
    <span
      className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold border shrink-0 ${
        PATIENT_TYPE_STYLES[patientTypeLabel!] ||
        PATIENT_TYPE_STYLES["Existing Patient"]
      }`}
    >
      {formatPatientTypeLabel(patientTypeLabel)}
    </span>
  );
}

function OldPatientAppointment({
  formData,
  setformData,
  error,
  setError,
  autoFocus = true,
  onViewPatient,
}: any) {
  const [patient, setpatient] = useState<any[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setsearch] = useState("");
  const [aciveId, setaciveId] = useState("");
  const [listOpen, setListOpen] = useState(true);
  const searchRef = useRef<HTMLInputElement>(null);
  const lastAutoPickRef = useRef<string>("");

  const handlePatientSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setListOpen(true);
  };

  const handlePatientSelect = (p: any) => {
    setaciveId(p._id);
    setListOpen(false);
    setformData((prevFormData: any) => ({
      ...prevFormData,
      patientId: p._id,
      fullName: p.fullName,
      gender: p.gender,
      mobileNo: p.mobileNo,
      address: p.address,
      patientTypeLabel: p.patientTypeLabel || "",
      registerdDate: p.registerdDate,
      createdAt: p.createdAt,
    }));
    if (setError) setError(false);
  };

  const clearPatient = () => {
    setaciveId("");
    setListOpen(true);
    setformData((prev: any) => ({
      ...prev,
      patientId: "",
      fullName: "",
      gender: "",
      mobileNo: "",
      address: "",
      patientTypeLabel: "",
      registerdDate: undefined,
      createdAt: undefined,
    }));
    window.setTimeout(() => searchRef.current?.focus(), 0);
  };

  useEffect(() => {
    const t = window.setTimeout(() => setsearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (!autoFocus) return;
    const t = window.setTimeout(() => searchRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [autoFocus]);

  useEffect(() => {
    baseUrl
      .get("appointment/findPatientsToAppointment", {
        headers: { status: true, keyword: search },
      })
      .then((response) => {
        setpatient(response.data.data ?? []);
      })
      .catch((err) => console.log(err));
  }, [search]);

  useEffect(() => {
    if (patient.length !== 1 || search.trim().length < 2) return;
    const key = `${search}:${patient[0]?._id}`;
    if (lastAutoPickRef.current === key) return;
    lastAutoPickRef.current = key;
    handlePatientSelect(patient[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- auto-pick once per search key
  }, [patient, search]);

  useEffect(() => {
    if (formData.patientId) setaciveId(formData.patientId);
  }, [formData.patientId]);

  const showPatientError = error && formData.patientId === "";
  const hasSelection = Boolean(formData.patientId && formData.fullName);
  const selectedVisitLabel = resolvePatientTypeLabelFromPatientAndTreatments({
    patientTypeLabel: formData.patientTypeLabel,
    registerdDate: formData.registerdDate,
    createdAt: formData.createdAt,
  });

  return (
    <div className="space-y-2">
      <div className="relative">
        <SearchIcon
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          sx={{ fontSize: 18 }}
        />
        <input
          ref={searchRef}
          type="search"
          autoFocus={autoFocus}
          autoComplete="off"
          onChange={handlePatientSearch}
          value={searchInput}
          placeholder="Name or reg no — type to search"
          className={`${UI.input} pl-10 py-2 ${
            showPatientError ? "border-red-400 ring-red-100" : ""
          }`}
          onKeyDown={(e) => {
            if (e.key === "Enter" && patient.length > 0) {
              e.preventDefault();
              handlePatientSelect(patient[0]);
            }
          }}
        />
      </div>

      {hasSelection && !listOpen ? (
        <div
          className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${
            showPatientError ? "border-red-400 bg-red-50/50" : "border-primary/25 bg-primary/5"
          }`}
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-sm font-semibold text-white">
            {formData.fullName?.charAt(0)?.toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <p className="text-sm font-semibold text-slate-900 truncate">
                {formData.fullName}
              </p>
              <PatientTypeBadges
                patientTypeLabel={selectedVisitLabel}
              />
            </div>
            <p className="text-xs text-slate-500 truncate">{formData.mobileNo}</p>
          </div>
          <CheckCircleIcon className="text-primary shrink-0" sx={{ fontSize: 18 }} />
          {onViewPatient ? (
            <button
              type="button"
              onClick={onViewPatient}
              className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              title="View patient"
            >
              <RemoveRedEyeIcon sx={{ fontSize: 14 }} />
              View patient
            </button>
          ) : null}
          <button
            type="button"
            onClick={clearPatient}
            className="shrink-0 text-xs font-semibold text-primary hover:underline"
          >
            Change
          </button>
        </div>
      ) : (
        <div
          className={`rounded-xl border max-h-[160px] overflow-y-auto bg-white ${
            showPatientError ? "border-red-400" : "border-slate-200/90"
          }`}
        >
          {patient.length === 0 ? (
            <div className="px-3 py-4 text-center text-slate-400">
              <PersonIcon sx={{ fontSize: 24 }} className="mb-0.5 opacity-60" />
              <p className="text-xs">Type to search patients</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {patient.map((p: any) => {
                const visitLabel = resolvePatientTypeLabelFromPatientAndTreatments(
                  p
                );
                return (
                  <li
                    key={p._id}
                    onClick={() => handlePatientSelect(p)}
                    className={`px-3 py-2 cursor-pointer flex items-center gap-2 ${
                      aciveId === p._id ? "bg-primary/10" : "hover:bg-slate-50"
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-medium text-slate-900 truncate block">
                        {p?.fullName}
                      </span>
                      <span className="text-xs text-slate-500">{p?.regNo}</span>
                    </div>
                    <PatientTypeBadges patientTypeLabel={visitLabel} />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {showPatientError ? (
        <p className="text-xs text-red-500">Select a patient</p>
      ) : null}
    </div>
  );
}

export default OldPatientAppointment;
